if (!customElements.get('products-set')) {
  customElements.define(
    'products-set',
    class ProductsSet extends HTMLElement {
      connectedCallback() {
        if (!this.initialized) {
          this.cards = Array.from(this.querySelectorAll('[data-products-set-card]'));
          this.submitButton = this.querySelector('[data-add-products-set]');
          this.buttonText = this.querySelector('[data-products-set-button-text]');
          this.errorElement = this.querySelector('[data-products-set-error]');
          this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          this.cardState = new Map();
          this.carouselRoot = this.querySelector('[data-products-set-carousel]');
          this.swiperEl = this.querySelector('[data-products-set-swiper]');
          this.nextEl = this.querySelector('[data-products-set-next]');
          this.scrollbarEl = this.querySelector('[data-products-set-scrollbar]');
          this.productsCount = Number(this.carouselRoot?.dataset.productsCount || this.cards.length);
          this.swiper = null;
          this.resizeFrame = null;
          this.swiperWaitFrames = 0;
          this.boundOnResize = this.onResize.bind(this);

          if (!this.cards.length || !this.submitButton) return;

          this.cards.forEach((card) => this.initializeCard(card));
          this.submitButton.addEventListener('click', this.handleSubmit.bind(this));
          this.updateSetState();
          this.initialized = true;
        }

        this.initCarousel();
        window.addEventListener('resize', this.boundOnResize);
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.boundOnResize);
        this.destroyCarousel();
      }

      onResize() {
        if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.initCarousel());
      }

      shouldUseCarousel() {
        if (this.productsCount <= 1) return false;
        if (window.matchMedia('(max-width: 749px)').matches) return true;
        if (window.matchMedia('(max-width: 1099px)').matches) return this.productsCount > 2;
        return this.productsCount > 4;
      }

      getSlidesPerView() {
        if (window.matchMedia('(min-width: 1100px)').matches) return 4.2;
        if (window.matchMedia('(min-width: 750px)').matches) return 2.2;
        return 1.2;
      }

      getSpaceBetween() {
        return window.matchMedia('(min-width: 750px)').matches ? 24 : 20;
      }

      initCarousel() {
        if (!this.swiperEl) return;

        if (typeof Swiper === 'undefined') {
          if (this.swiperWaitFrames < 120) {
            this.swiperWaitFrames += 1;
            requestAnimationFrame(() => this.initCarousel());
          }
          return;
        }

        const useCarousel = this.shouldUseCarousel();
        this.classList.toggle('products-set--carousel', useCarousel);
        this.classList.toggle('products-set--static', !useCarousel);

        if (!useCarousel) {
          this.destroyCarousel();
          return;
        }

        const slidesPerView = this.getSlidesPerView();
        const spaceBetween = this.getSpaceBetween();
        this.style.setProperty('--products-set-peek', `${(0.2 / slidesPerView) * 100}%`);

        if (this.swiper) {
          this.swiper.params.slidesPerView = slidesPerView;
          this.swiper.params.spaceBetween = spaceBetween;
          this.swiper.update();
          return;
        }

        const swiperOptions = {
          slidesPerView,
          spaceBetween,
          watchOverflow: true,
          resistanceRatio: 0.65,
        };

        if (this.nextEl) {
          swiperOptions.navigation = { nextEl: this.nextEl };
        }

        if (this.scrollbarEl) {
          swiperOptions.scrollbar = {
            el: this.scrollbarEl,
            draggable: true,
            hide: false,
          };
        }

        this.swiper = new Swiper(this.swiperEl, swiperOptions);
      }

      destroyCarousel() {
        if (!this.swiper) return;
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      initializeCard(card) {
        const variantsElement = card.querySelector('[data-product-variants]');
        let variants = [];

        try {
          variants = JSON.parse(variantsElement?.textContent || '[]');
        } catch (error) {
          console.error('Unable to read products set variants.', error);
        }

        const selects = Array.from(card.querySelectorAll('[data-option-position]'));
        const defaultVariantId = Number(card.dataset.defaultVariant) || null;
        const defaultVariant = variants.find((variant) => Number(variant.id) === defaultVariantId);

        this.cardState.set(card, {
          variants,
          selects,
          selectedVariant: defaultVariant?.available ? defaultVariant : null,
        });

        selects.forEach((select) => {
          select.addEventListener('change', () => this.handleOptionChange(card));
        });

        this.updateCard(card);
      }

      handleOptionChange(card) {
        const state = this.cardState.get(card);
        const selectedOptions = state.selects.map((select) => select.value);
        const hasAllOptions = selectedOptions.every(Boolean);

        state.selectedVariant = hasAllOptions
          ? state.variants.find(
              (variant) =>
                variant.available &&
                variant.options.every((option, index) => option === selectedOptions[index])
            ) || null
          : null;

        this.updateOptionAvailability(state);
        this.updateCard(card, hasAllOptions);
        this.updateSetState();
      }

      updateOptionAvailability(state) {
        const selectedOptions = state.selects.map((select) => select.value);

        state.selects.forEach((select, optionIndex) => {
          Array.from(select.options).forEach((option) => {
            if (!option.value) return;

            option.disabled = !state.variants.some((variant) => {
              if (!variant.available || variant.options[optionIndex] !== option.value) return false;

              return selectedOptions.every(
                (selectedValue, index) =>
                  index >= optionIndex || !selectedValue || variant.options[index] === selectedValue
              );
            });
          });
        });
      }

      updateCard(card, hasAllOptions = false) {
        const state = this.cardState.get(card);
        const status = card.querySelector('[data-selection-status]');
        const isSelected = Boolean(state.selectedVariant);

        card.classList.toggle('is-selected', isSelected);

        if (!status || !state.selects.length) return;

        if (isSelected) {
          status.textContent = `${this.dataset.selectedLabel}: ${state.selectedVariant.title}`;
        } else if (hasAllOptions) {
          status.textContent = this.dataset.unavailableLabel;
        } else {
          status.textContent = '';
        }
      }

      updateSetState() {
        const isComplete =
          this.cards.length > 0 &&
          this.cards.every((card) => Boolean(this.cardState.get(card)?.selectedVariant));

        this.submitButton.disabled = !isComplete;
        this.submitButton.setAttribute('aria-disabled', String(!isComplete));
        this.buttonText.textContent = isComplete ? this.dataset.addLabel : this.dataset.chooseLabel;
      }

      async handleSubmit() {
        if (this.submitButton.disabled) return;

        const items = this.cards.map((card) => ({
          id: this.cardState.get(card).selectedVariant.id,
          quantity: 1,
        }));

        this.setLoading(true);
        this.showError();

        try {
          const body = { items };
          const sectionIds =
            this.cart && typeof this.cart.getSectionsToRender === 'function'
              ? this.cart.getSectionsToRender().map((section) => section.id)
              : [];

          if (this.cart) {
            this.cart.setActiveElement?.(this.submitButton);
          }

          const cartAddUrl = routes.cart_add_url.endsWith('.js')
            ? routes.cart_add_url
            : `${routes.cart_add_url}.js`;
          const response = await fetch(cartAddUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(body),
          });
          const result = await response.json();

          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || this.dataset.errorLabel);
          }

          items.forEach((item) => {
            document.dispatchEvent(
              new CustomEvent('variant:add', {
                detail: {
                  variant: { id: item.id },
                  quantity: item.quantity,
                  formElement: this,
                  sectionId: this.dataset.sectionId,
                },
              })
            );
          });

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'products-set',
              productVariantId: items[0].id,
            });
          }

          if (this.cart && typeof this.cart.renderContents === 'function') {
            const sectionsResponse = await fetch(
              `${routes.cart_url}?sections=${encodeURIComponent(sectionIds.join(','))}`
            );
            const sections = await sectionsResponse.json();

            if (!sectionsResponse.ok) {
              throw new Error(this.dataset.errorLabel);
            }

            this.cart.renderContents({
              id: items[0].id,
              sections,
            });

            const cartDrawer = this.cart.querySelector('.cart-drawer');
            cartDrawer?.classList.remove('notification-open');
            cartDrawer?.querySelectorAll('.cart-item.active').forEach((item) => {
              item.classList.remove('active');
            });
          } else {
            window.location.assign(routes.cart_url);
            return;
          }

          this.dispatchCartChange();
          this.resetSelections();
        } catch (error) {
          console.error(error);
          this.showError(error.message || this.dataset.errorLabel);
        } finally {
          this.setLoading(false);
        }
      }

      async dispatchCartChange() {
        try {
          const response = await fetch(`${routes.cart_url}.js`);
          const cart = await response.json();

          document.dispatchEvent(
            new CustomEvent('cart:change', {
              detail: {
                cart,
                sectionId: this.dataset.sectionId,
              },
            })
          );
        } catch (error) {
          console.error('Unable to refresh cart state.', error);
        }
      }

      resetSelections() {
        this.cards.forEach((card) => {
          const state = this.cardState.get(card);

          state.selects.forEach((select) => {
            select.selectedIndex = 0;
          });

          state.selectedVariant =
            state.selects.length === 0
              ? state.variants.find(
                  (variant) => Number(variant.id) === Number(card.dataset.defaultVariant) && variant.available
                ) || null
              : null;

          this.updateOptionAvailability(state);
          this.updateCard(card);
        });

        this.updateSetState();
      }

      setLoading(isLoading) {
        this.submitButton.classList.toggle('loading', isLoading);
        if (isLoading) {
          this.submitButton.disabled = true;
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else {
          this.updateSetState();
        }
      }

      showError(message = '') {
        if (!this.errorElement) return;

        this.errorElement.textContent = message;
        this.errorElement.hidden = !message;
      }
    }
  );
}
